import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {useEffect} from '@wordpress/element';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {type TokenResult, type Payments, type ACH, type PlaidEventName, type SqEvent, type TokenizationEvent} from '@square/web-payments-sdk-types';
import {z} from '@peachpay/checkout-blocks';
import {getErrorString} from '../../../../../../frontend/@shared/ts/error';
import {formatAmount} from '../currency';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';

const SettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	fallback_currency: z.string().nullable(),
	supports: z.string().array(),
	square: z.object({
		application_id: z.string(),
		location_id: z.string(),
	}),
});

const parsedResult = SettingsSchema.safeParse(getSetting('peachpay_square_ach_data'));
if (!parsedResult.success) {
	throw new Error(`Payment method "peachpay_square_ach" setup aborted. Invalid settings.\n\n${parsedResult.error.message}`);
}

const settings = parsedResult.data;
const title = decodeEntities(settings.title);
const description = decodeEntities(settings.description);

const iconStyle = {maxHeight: '25px'} as const;
const descriptionStyle = {textAlign: 'left' as const, margin: '0.5rem 0 0'} as const;
const hrStyle = {margin: '0.5rem 0'} as const;
const smallTextStyle = {textAlign: 'left' as const, margin: '0', fontSize: 'smaller'} as const;

if (!window.Square) {
	throw new Error('Payment method "peachpay_square_ach" setup aborted. Square SDK not loaded.');
}

let payments: Payments;
try {
	payments = window.Square.payments(settings.square.application_id, settings.square.location_id);
} catch (err) {
	throw new Error(`Payment method "peachpay_square_ach" setup aborted. Could not create Payments instance.\n\n${getErrorString(err)}`);
}

/**
 * Component to render the payment method label
 */
function Label(props: PaymentMethodInterface) {
	const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
	return (
		<>
			<PaymentMethodLabel text={title} />
			<PaymentMethodIcons className='pp-label-icon' icons={[{
				id: 'peachpay_square_ach',
				src: settings.icon,
				alt: title,
			}]} />
		</>
	);
}

/**
 * Component to render the ACH payment method body
 */
function Content({billing, eventRegistration, emitResponse}: PaymentMethodInterface) {
	const {
		onPaymentSetup,
		onCheckoutFail,
	} = eventRegistration;

	/**
	 * Handle payment tokenization and verification right before the order is submitted.
	 */
	useEffect(() => {
		const unsubscribe = onPaymentSetup(async () => {
			const accountHolderName = `${billing.billingAddress.first_name} ${billing.billingAddress.last_name}`.replace(/\s+/g, ' ').trim();
			if (!accountHolderName) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('First and last name are required for ACH payments.', 'peachpay-for-woocommerce'),
				};
			}

			const amount = billing.cartTotal.value;
			const currency = billing.currency.code;
			if (!amount || Number(amount) <= 0) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('A valid order total is required for ACH payments.', 'peachpay-for-woocommerce'),
				};
			}

			// Square ACH redirectURI must not contain query parameters.
			const redirectURI = window.location.origin + window.location.pathname;

			let ach!: ACH;
			try {
				ach = await payments.ach({
					redirectURI,
					transactionId: crypto.randomUUID(),
				});
			} catch (err) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: getErrorString(err),
				};
			}

			const tokenizeOptions = {
				accountHolderName,
				intent: 'CHARGE',
				// WC Blocks total is in smallest units; Square ACH expects decimal amount.
				amount: formatAmount(String(amount), '0', currency),
				currency,
			} as unknown as Parameters<typeof ach.tokenize>[0];

			let tokenResult: TokenResult;
			try {
				tokenResult = await new Promise<TokenResult>((resolve, reject) => {
					let settled = false;
					const onTokenization = (event: SqEvent<TokenizationEvent>) => {
						if (settled) {
							return;
						}

						settled = true;
						ach.removeEventListener('ontokenization' as PlaidEventName.ON_TOKENIZATION, onTokenization);

						const {error, tokenResult} = event.detail ?? {};
						if (error) {
							reject(error);
							return;
						}

						if (!tokenResult) {
							reject(new Error('Tokenization failed. Missing tokenization result.'));
							return;
						}

						resolve(tokenResult);
					};

					ach.addEventListener('ontokenization' as PlaidEventName.ON_TOKENIZATION, onTokenization);

					ach.tokenize(tokenizeOptions).catch(error => {
						if (settled) {
							return;
						}

						settled = true;
						ach.removeEventListener('ontokenization' as PlaidEventName.ON_TOKENIZATION, onTokenization);
						reject(error);
					});
				});
			} catch (err) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: getErrorString(err),
				};
			}

			if (tokenResult.status === 'Cancel' || tokenResult.status === 'Abort') {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('Payment was cancelled', 'peachpay-for-woocommerce'),
				};
			}

			if (tokenResult.status !== 'OK' || !tokenResult.token) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: ((tokenResult.errors ?? []) as Array<{message: string}>).map(err => err.message).join('<br/>')
						|| __('Tokenization failed. Please try again or use a different payment method.', 'peachpay-for-woocommerce'),
				};
			}

			return {
				type: emitResponse.responseTypes.SUCCESS,
				meta: {
					paymentMethodData: {
						peachpay_square_source_id: tokenResult.token,
					},
				},
			};
		});
		return unsubscribe;
	}, [
		billing.billingAddress.first_name,
		billing.billingAddress.last_name,
		billing.cartTotal.value,
		billing.currency.code,
		onPaymentSetup,
	]);

	/**
	 * Handle checkout failure after the order was submitted. Failure may or may
	 * not be payment related.
	 */
	useEffect(() => {
		const unsubscribe = onCheckoutFail(async (data: CheckoutAfterProcessingWithErrorEventData) => ({
			type: emitResponse.responseTypes.ERROR,
			message: (data.processingResponse?.paymentDetails?.['message']) ?? __('An unknown error occured while processing the payment.', 'peachpay-for-woocommerce'),
			messageContext: emitResponse.noticeContexts.PAYMENTS,
		}));
		return unsubscribe;
	}, [onCheckoutFail]);

	/**
	 * If the payment method does not support the current currency we will show
	 * a fallback button to allow changing the currency.
	 *
	 * Note: Due to how our currency switcher works the `fallback_currency` setting
	 * is only defined when the payment does not support the current currency and
	 * does not change after page load.
	 */
	if (settings.fallback_currency) {
		return <CurrencyFallback name={title} currency={settings.fallback_currency} />;
	}

	return (
		<>
			<img style={iconStyle} src={settings.icon} alt=''></img>
			<p style={descriptionStyle}>{title} {__('selected for checkout.', 'peachpay-for-woocommerce')}</p>
			<hr style={hrStyle}/>
			<p style={smallTextStyle}>{description}</p>
		</>
	);
}

registerPaymentMethod({
	name: 'peachpay_square_ach',
	ariaLabel: title,
	canMakePayment(data) {
		return data.paymentMethods.includes('peachpay_square_ach');
	},
	// @ts-expect-error WC will clone these components and pass the props when used.
	label: <Label />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	content: <Content />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	edit: <Content />,
	supports: {
		features: settings.supports,
	},
});
